import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';

const formatCount = (n) => String(n).padStart(2, '0');

const SummaryStat = ({ label, value, highlight }) => (
  <div className="text-center">
    <p
      className={`text-lg font-bold leading-none ${
        highlight ? 'text-[#0f2a45]' : 'text-gray-700'
      }`}>
      {formatCount(value)}
    </p>
    <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
  </div>
);

const UserTaskCard = ({ user, expanded, onToggle, tagClassName }) => (
  <div className="rounded-xl border border-gray-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 p-4 text-left">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[#0f2a45]">{user.name}</p>
        <p className="text-xs text-gray-500">{user.role}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <div className="text-right">
          <p className="text-xs font-semibold text-gray-600">
            {user.completed}/{user.total}
          </p>
          <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${user.barColor}`}
              style={{ width: `${user.percent}%` }}
            />
          </div>
          <p className="mt-0.5 text-[10px] text-gray-400">{user.percent}%</p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </div>
    </button>

    {expanded && user.tasks.length > 0 && (
      <div className="space-y-4 border-t border-gray-100 px-4 pb-4 pt-3">
        {user.tasks.map((task) => (
          <div key={task.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-[#0f2a45]">{task.title}</p>
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                  task.tagClassName || tagClassName
                }`}>
                {task.status || task.tag}
              </span>
            </div>
            {task.dueDate ? (
              <p className="mt-1 text-xs text-gray-500">Due {task.dueDate}</p>
            ) : null}
            {task.blocking ? (
              <div className="mt-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2">
                <p className="text-xs text-rose-700">
                  <span className="font-semibold">Blocking:</span> {task.blocking}
                </p>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    )}
  </div>
);

const DummyTaskAccountabilityPanel = ({
  summary,
  users,
  tagClassName,
  defaultExpandedUserId,
  emptyMessage = 'No assignments yet.'
}) => {
  const [expandedUsers, setExpandedUsers] = useState(() =>
    defaultExpandedUserId ? { [defaultExpandedUserId]: true } : {}
  );

  const toggleUser = (id) => {
    setExpandedUsers((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Users className="h-4 w-4 text-[#1E4A53]" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E4A53]">
          Task Accountability
        </h3>
      </div>

      <div className="mb-5 grid grid-cols-4 gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3">
        <SummaryStat label="Pending" value={summary.pending} />
        <SummaryStat label="Delayed" value={summary.delayed} highlight />
        <SummaryStat label="Done" value={summary.done} />
        <SummaryStat label="Total" value={summary.total} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {users.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">{emptyMessage}</p>
        ) : (
          users.map((user) => (
            <UserTaskCard
              key={user.id}
              user={user}
              expanded={Boolean(expandedUsers[user.id])}
              onToggle={() => toggleUser(user.id)}
              tagClassName={tagClassName}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default DummyTaskAccountabilityPanel;
